import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FiPackage, FiShoppingBag } from 'react-icons/fi';
import Navbar from '../components/Navbar';
// import Footer from '../components/Footer';
import FloatingButtons from '../components/FloatingButtons';
import { userApi } from '../../api/userApi';
import { toast } from 'react-hot-toast';

const BrandCard = ({ brand }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    whileHover={{ y: -5 }}
    className="bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
  >
    <Link to={`/products?brand=${encodeURIComponent(brand.name)}`}>
      <div className="h-[300px] overflow-hidden"> {/* Fixed height for image container */}
        <img
          src={brand.image}
          alt={brand.name}
          className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
          onError={(e) => {
            e.target.src = 'https://via.placeholder.com/300x300?text=Brand+Image'; // Fallback image
          }}
        />
      </div>
      <div className="p-6 h-[120px] flex flex-col justify-between"> {/* Fixed height for content */}
        <div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{brand.name}</h3>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-[#2C3E50] hover:text-[#34495E] transition-colors">
            View Collection →
          </span>
        </div>
      </div>
    </Link>
  </motion.div>
);

const EmptyState = () => (
  <div className="flex flex-col items-center justify-center h-[60vh]">
    <FiPackage size={100} className="text-gray-300 mb-4" />
    <h2 className="text-2xl font-bold text-gray-700 mb-4">No Brands Found</h2>
    <p className="text-gray-600 text-center mb-8">It looks like there are no brands available at the moment.</p>
    <Link to="/products" className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 flex items-center">
      <FiShoppingBag className="mr-2" />
      Shop Now
    </Link>
  </div>
);

const BrandsPage = () => {
  const [brands, setBrands] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setIsLoading(true);
        const response = await userApi.getBrands();
        if (Array.isArray(response)) {
          setBrands(response);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error fetching brands:', error);
        setError('Failed to load brands');
        toast.error('Failed to load brands');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBrands();
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Brands | Panchali Boutique</title>
        <meta 
          name="description" 
          content="Discover our curated collection of premium ethnic wear brands at Panchali Boutique" 
        />
      </Helmet>

      <div className="flex flex-col min-h-screen">
        <Navbar />
        
        <main className="flex-grow bg-[#FAF9F6] pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-12"
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Our Brands</h1>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Discover our curated collection of premium ethnic wear brands, 
                each offering unique designs and exceptional quality.
              </p>
            </motion.div>

            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {[1, 2, 3, 4].map((index) => (
                  <div key={index} className="animate-pulse">
                    <div className="bg-gray-200 h-[300px] rounded-t-lg"></div>
                    <div className="bg-white p-6 h-[150px] rounded-b-lg">
                      <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                      <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : error ? (
              <div className="text-center text-red-600">
                <p>{error}</p>
              </div>
            ) : brands.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {brands.map((brand) => (
                  <BrandCard key={brand.id} brand={brand} />
                ))}
              </div>
            ) : (
              <EmptyState />
            )}
          </div>
        </main>

        {/* <Footer /> */}
        <FloatingButtons />
      </div>
    </>
  );
};

export default BrandsPage;

